<script lang="ts">
  import { Input } from '@/lib/components/ui/input';
  import { Separator } from '@/lib/components/ui/separator';
  import * as Select from '$lib/components/ui/select';
  import Button from '@/lib/components/ui/button/button.svelte';
  import Switch from '@/lib/components/ui/switch/switch.svelte';
  import { cn } from '@/lib/utils';
  import LocationPickerDialog from './LocationPickerDialog.svelte';
  import ActivityMemberFilter from './ActivityMemberFilter.svelte';
  import ActivityTagFilter from './ActivityTagFilter.svelte';
  import {
    ActivityFilterFields,
    type ActivityOwnerOption,
    activityDateRangePresets,
    activityDifficultyPresets,
    activityDurationPresets,
    activityGpsRadiusOptions,
    activityFocusOptions,
    activitySortDirOptions,
    activitySortOptions,
    activitySpecificityOptions,
    activityTypeOptions,
    difficultyOptions,
    type LocationFilterKey,
  } from './ActivityFilterFields';

  export let fields: ActivityFilterFields;
  /** The coach/squad roster this dashboard can be narrowed to; empty on the personal dashboard. */
  export let owners: ActivityOwnerOption[] = [];
  export let ownersTitle: string = 'Athletes';
  /**
   * Sections a host page doesn't want, by name: 'sort', 'search', 'dates', 'location'.
   * The Progression tab reuses this filter but has its own date range control and no list to sort.
   */
  export let hide: string[] = [];

  // The two location filters differ only in which of an activity's positions have to fall inside
  // the circle, so they share their markup and one picker dialog.
  const locationFilters: { key: LocationFilterKey; title: string; placeholder: string; hint: string }[] = [
    {
      key: 'startsNear',
      title: 'Starts near',
      placeholder: 'Starts near...',
      hint: 'Keeps the activities that set off from here.',
    },
    {
      key: 'goesThrough',
      title: 'Goes through',
      placeholder: 'Goes through...',
      hint: 'Keeps the activities that passed here at any point.',
    },
  ];

  let pickerOpen = false;
  let pickerFilter = locationFilters[0];

  function openPicker(filter: (typeof locationFilters)[number]) {
    pickerFilter = filter;
    pickerOpen = true;
  }
</script>

{#if fields}
  <div class="flex flex-col gap-2">
    {#if !hide.includes('sort')}
    <div>
      <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">Sort By</h3>
      <div class="grid grid-cols-2 gap-2">
        <Select.Root portal="#shadcn-ui-dialog-target" bind:selected={fields.sortBy} items={activitySortOptions} onSelectedChange={fields.onSortByChange}>
          <Select.Trigger class={cn(fields.isFilterApplied('sortBy') ? 'border-2 border-blue-400' : '')}>
            <Select.Value />
          </Select.Trigger>
          <Select.Content class="overflow-auto max-h-48">
            {#each activitySortOptions as sort}
              <Select.Item value={sort.value}>{sort.label}</Select.Item>
            {/each}
          </Select.Content>
        </Select.Root>
        <Select.Root portal="#shadcn-ui-dialog-target" bind:selected={fields.sortDirection} items={activitySortDirOptions} onSelectedChange={fields.onSortDirectionChange}>
          <Select.Trigger class={cn(fields.isFilterApplied('sortDirection') ? 'border-2 border-blue-400' : '')}>
            <Select.Value />
          </Select.Trigger>
          <Select.Content class="overflow-auto max-h-48">
            {#each activitySortDirOptions as dir}
              <Select.Item value={dir.value}>{dir.label}</Select.Item>
            {/each}
          </Select.Content>
        </Select.Root>
      </div>
    </div>

    <Separator />

    {/if}
    {#if !hide.includes('search')}
    <div>
      <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">Search</h3>
      <Input
        placeholder="Name, description, sport..."
        class={cn(fields.isFilterApplied('searchTerm') ? 'border-2 border-blue-400' : '')}
        bind:value={fields.searchTerm}
        on:input={fields.executeFilterDebouncer}
      />
    </div>

    <Separator />

    {/if}
    <div class="flex flex-row flex-wrap gap-2 items-center">
      {#each activityTypeOptions as activityType}
        <Button
          variant={fields.activityType.value === activityType.value ? 'default' : 'outline'}
          size="sm"
          class="rounded-full"
          on:click={() => { fields.activityType = activityType; fields.apply(); }}
        >
          {activityType.label}
        </Button>
      {/each}
    </div>

    <Separator />

    {#if !hide.includes('dates')}
    <div>
      <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">Date Range</h3>
      <div class="flex flex-row flex-wrap gap-1 items-center mb-2">
        {#each activityDateRangePresets as preset}
          <Button variant="outline" size="sm" class="rounded-full text-xs h-7 px-2" on:click={() => fields.setDateRangePreset(preset.value)}>
            {preset.label}
          </Button>
        {/each}
        {#if fields.isFilterApplied('dateFrom') || fields.isFilterApplied('dateTo')}
          <Button variant="ghost" size="sm" class="text-xs h-7 px-2" on:click={() => fields.setDateRangePreset(null)}>
            Clear
          </Button>
        {/if}
      </div>
      <div class="grid grid-cols-2 gap-2">
        <Input type="date" title="From" class={cn(fields.isFilterApplied('dateFrom') ? 'border-2 border-blue-400' : '')} bind:value={fields.dateFrom} on:change={fields.onBooleanOrCategoricalFilterChange} />
        <Input type="date" title="To" class={cn(fields.isFilterApplied('dateTo') ? 'border-2 border-blue-400' : '')} bind:value={fields.dateTo} on:change={fields.onBooleanOrCategoricalFilterChange} />
      </div>
    </div>

    <Separator />

    {/if}
    {#if !hide.includes('location')}
    <div>
      <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">Location</h3>
      <div class="flex flex-col gap-2">
        {#each locationFilters as filter}
          <div class="flex flex-row items-center gap-1 min-w-0">
            <Button
              variant="outline"
              class={cn(
                'flex-1 min-w-0 justify-start font-normal',
                fields.isFilterApplied(filter.key) ? 'border-2 border-blue-400' : ''
              )}
              title={filter.hint}
              on:click={() => openPicker(filter)}
            >
              <i class="fa-solid fa-location-dot text-xs mr-2 text-muted-foreground" aria-hidden="true"></i>
              <span class={cn('truncate', fields.locationLabel(filter.key) ? '' : 'text-muted-foreground')}>
                {fields.locationLabel(filter.key) || filter.placeholder}
              </span>
            </Button>
            {#if fields.isFilterApplied(filter.key)}
              <Button
                variant="ghost"
                size="icon"
                class="shrink-0"
                title="Clear {filter.title.toLowerCase()}"
                on:click={() => { fields.setLocation(filter.key, null); fields = fields; }}
              >
                <i class="fa-solid fa-xmark text-muted-foreground" aria-hidden="true"></i>
              </Button>
            {/if}
          </div>
        {/each}
        {#if fields.isFilterApplied('startsNear') || fields.isFilterApplied('goesThrough')}
          <div class="flex flex-row items-center gap-2">
            <span class="text-xs text-muted-foreground shrink-0">Within</span>
            <Select.Root
              portal="#shadcn-ui-dialog-target"
              bind:selected={fields.gpsRadius}
              items={activityGpsRadiusOptions}
              onSelectedChange={fields.onGpsRadiusChange}
            >
              <Select.Trigger>
                <Select.Value />
              </Select.Trigger>
              <Select.Content class="overflow-auto max-h-48">
                {#each activityGpsRadiusOptions as radius}
                  <Select.Item value={radius.value}>{radius.label}</Select.Item>
                {/each}
              </Select.Content>
            </Select.Root>
          </div>
        {/if}
      </div>
    </div>
    {/if}

    {#if owners.length}
      <Separator />

      <ActivityMemberFilter bind:fields {owners} title={ownersTitle} />
    {/if}

    <!-- The sections above each bring their own divider, so with all of them hidden this would double up. -->
    {#if !(hide.includes('dates') && hide.includes('location') && !owners.length)}
      <Separator />
    {/if}

    <ActivityTagFilter bind:fields />

    <Separator />

    <div>
      <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">Duration (minutes)</h3>
      <div class="flex flex-row flex-wrap gap-1 items-center mb-2">
        {#each activityDurationPresets as preset}
          <Button variant="outline" size="sm" class="rounded-full text-xs h-7 px-2" on:click={() => fields.setDurationPreset(preset.value)}>
            {preset.label}
          </Button>
        {/each}
        {#if fields.isFilterApplied('minDuration') || fields.isFilterApplied('maxDuration')}
          <Button variant="ghost" size="sm" class="text-xs h-7 px-2" on:click={() => fields.setDurationPreset([null, null])}>
            Clear
          </Button>
        {/if}
      </div>
      <div class="grid grid-cols-2 gap-2">
        <Input type="number" placeholder="Min" class={cn(fields.isFilterApplied('minDuration') ? 'border-2 border-blue-400' : '')} bind:value={fields.minDuration} on:input={fields.onNumericFilterChange} />
        <Input type="number" placeholder="Max" class={cn(fields.isFilterApplied('maxDuration') ? 'border-2 border-blue-400' : '')} bind:value={fields.maxDuration} on:input={fields.onNumericFilterChange} />
      </div>
    </div>

    <div>
      <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">Difficulty</h3>
      <div class="flex flex-row flex-wrap gap-1 items-center mb-2">
        {#each activityDifficultyPresets as preset}
          <Button variant="outline" size="sm" class="rounded-full text-xs h-7 px-2" on:click={() => fields.setDifficultyPreset(preset.value)}>
            {preset.label}
          </Button>
        {/each}
        {#if fields.isFilterApplied('minDifficulty') || fields.isFilterApplied('maxDifficulty')}
          <Button variant="ghost" size="sm" class="text-xs h-7 px-2" on:click={() => fields.setDifficultyPreset([null, null])}>
            Clear
          </Button>
        {/if}
      </div>
      <div class="grid grid-cols-2 gap-2">
        <Select.Root portal="#shadcn-ui-dialog-target" bind:selected={fields.minDifficulty} items={difficultyOptions} onSelectedChange={fields.onBooleanOrCategoricalFilterChange}>
          <Select.Trigger class={cn(fields.isFilterApplied('minDifficulty') ? 'border-2 border-blue-400' : '')}>
            <Select.Value />
          </Select.Trigger>
          <Select.Content class="overflow-auto max-h-48">
            {#each difficultyOptions as difficulty}
              <Select.Item value={difficulty.value}>{difficulty.label}</Select.Item>
            {/each}
          </Select.Content>
        </Select.Root>
        <Select.Root portal="#shadcn-ui-dialog-target" bind:selected={fields.maxDifficulty} items={difficultyOptions} onSelectedChange={fields.onBooleanOrCategoricalFilterChange}>
          <Select.Trigger class={cn(fields.isFilterApplied('maxDifficulty') ? 'border-2 border-blue-400' : '')}>
            <Select.Value />
          </Select.Trigger>
          <Select.Content class="overflow-auto max-h-48">
            {#each difficultyOptions as difficulty}
              <Select.Item value={difficulty.value}>{difficulty.label}</Select.Item>
            {/each}
          </Select.Content>
        </Select.Root>
      </div>
    </div>

    <div>
      <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">XSS</h3>
      <div class="grid grid-cols-2 gap-2">
        <Input type="number" placeholder="Min" class={cn(fields.isFilterApplied('minXss') ? 'border-2 border-blue-400' : '')} bind:value={fields.minXss} on:input={fields.onNumericFilterChange} />
        <Input type="number" placeholder="Max" class={cn(fields.isFilterApplied('maxXss') ? 'border-2 border-blue-400' : '')} bind:value={fields.maxXss} on:input={fields.onNumericFilterChange} />
      </div>
    </div>

    <Separator />

    <div>
      <div class="grid grid-cols-2 gap-2">
        <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">Specificity</h3>
        <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">Focus</h3>
        <Select.Root portal="#shadcn-ui-dialog-target" bind:selected={fields.selectedSpecificity} items={activitySpecificityOptions} onSelectedChange={fields.onBooleanOrCategoricalFilterChange}>
          <Select.Trigger class={cn(fields.isFilterApplied('selectedSpecificity') ? 'border-2 border-blue-400' : '')}>
            <Select.Value />
          </Select.Trigger>
          <Select.Content class="overflow-auto max-h-48">
            {#each activitySpecificityOptions as specificity}
              <Select.Item value={specificity.value}>{specificity.label}</Select.Item>
            {/each}
          </Select.Content>
        </Select.Root>
        <Select.Root portal="#shadcn-ui-dialog-target" bind:selected={fields.selectedFocus} items={activityFocusOptions} onSelectedChange={fields.onBooleanOrCategoricalFilterChange}>
          <Select.Trigger class={cn(fields.isFilterApplied('selectedFocus') ? 'border-2 border-blue-400' : '')}>
            <Select.Value />
          </Select.Trigger>
          <Select.Content class="overflow-auto max-h-48">
            {#each activityFocusOptions as focus}
              <Select.Item value={focus.value}>{focus.label}</Select.Item>
            {/each}
          </Select.Content>
        </Select.Root>
      </div>
    </div>

    <Separator />

    <div class="flex flex-col gap-1">
      {#if !fields.forceFavouritesOnly}
        <div class="flex flex-row gap-2 items-center my-2">
          <Switch bind:checked={fields.favouritesOnly} on:click={fields.onBooleanOrCategoricalFilterChange} />
          <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">Favourites</h3>
        </div>
      {/if}
      <div class="flex flex-row gap-2 items-center my-2">
        <Switch bind:checked={fields.breakthroughsOnly} on:click={fields.onBooleanOrCategoricalFilterChange} />
        <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">Breakthroughs</h3>
      </div>
      <div class="flex flex-row gap-2 items-center my-2">
        <Switch bind:checked={fields.groupRidesOnly} on:click={fields.onBooleanOrCategoricalFilterChange} />
        <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">Group Activities</h3>
      </div>
      {#if fields.canShowHidden}
        <div class="flex flex-row gap-2 items-center my-2">
          <Switch bind:checked={fields.showHidden} on:click={fields.onBooleanOrCategoricalFilterChange} />
          <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0" title="Also list activities left out of your fitness calculations">Show Hidden</h3>
        </div>
      {/if}
    </div>

  </div>

  <LocationPickerDialog
    bind:open={pickerOpen}
    title={pickerFilter.title}
    hint={pickerFilter.hint}
    point={fields.locationPoint(pickerFilter.key)}
    radiusKm={fields.gpsRadius.value}
    onConfirm={(place, radius) => { fields.setLocation(pickerFilter.key, place, radius); fields = fields; }}
  />
{/if}
